import { normalizeErrorString } from '@medplum/core'
import type {
  Bundle,
  Practitioner,
  Reference,
  Resource,
  ResourceType,
} from '@medplum/fhirtypes'
import {
  ExtendedProvenance,
  ProvenanceParticipantType,
  provenanceActivityQueuedEditsReviewed,
} from '@seen/core/models/provenance'
import { QueuedEditsReviewTask } from '@seen/core/models/tasks/standard/queuedEditsReviewTask'
import {
  getBundleAiSummary,
  getBundleAiSummaryOmittedEntries,
  setBundleAiSummary,
  setBundleAiSummaryOmittedEntries,
} from '@seen/core/queued-edits/ai-summaries'
import { appliedFromVersionRefs } from '@seen/core/queued-edits/applied-from-versions'
import { rebaseProposalApplyBundle } from '@seen/core/queued-edits/apply-rebase'
import type { BundleToChangeSetsInput } from '@seen/core/queued-edits/bundle-to-change-sets'
import {
  appliedReferencesByFullUrl,
  buildProposalCommitOutcome,
  type ProposalCommitOutcome,
} from '@seen/core/queued-edits/commit-outcome'
import {
  entryFromVersions,
  getEntryDecisions,
  getFinalCommittedAt,
  recordEntryDecisions,
  setFinalCommitter,
  stripEntryDecisions,
} from '@seen/core/queued-edits/decisions'
import { setEntryIntents } from '@seen/core/queued-edits/entry-intents'
import type { ResourceDecision } from '@seen/core/queued-edits/model'
import {
  createReferenceStringWithHistory,
  parseHistoryReferenceString,
} from '@seen/core/references'
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import { isEqual } from 'lodash-es'
import { type JSX, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Spinner } from '#src/components/core/Loader.tsx'
import {
  QUEUED_EDITS_ALL_TAB,
  QueuedEditsReview,
  type QueuedEditsReviewPhase,
} from '#src/components/queued-edits/QueuedEditsReview.tsx'
import { QueuedEditsReviewSummary } from '#src/components/queued-edits/QueuedEditsReviewSummary.tsx'
import { useBundleChangeSets } from '#src/components/queued-edits/useBundleChangeSets.ts'
import { useBundleDecisionWriter } from '#src/components/queued-edits/useBundleDecisionWriter.ts'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { cacheKeys } from '#src/hooks/cacheKeys.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

// Attempts for the post-apply bundle finalization write, matching
// useBundleDecisionWriter's conflict-retry budget — concurrent decision writes
// can race the finalize between its read and update.
const FINALIZE_CONFLICT_RETRIES = 3

// References ("ResourceType/id") of the existing resources a proposed bundle
// mutates — the `current` map the differ diffs against.
function mutatedReferences(bundle: Bundle): string[] {
  const refs = new Set<string>()
  for (const entry of bundle.entry ?? []) {
    const method = entry.request?.method
    const url = entry.request?.url
    if (!url || method === 'POST' || method === undefined) {
      continue
    }
    // Conditional (search-based) urls can't be resolved to a single current
    // resource — the differ degrades gracefully without one.
    const [resourceType, id] = url.split('?')[0]?.split('/') ?? []
    if (resourceType && id) {
      refs.add(`${resourceType}/${id}`)
    }
  }
  return [...refs]
}

// Read the current state of each referenced resource. Already-deleted/missing
// resources are fine — callers degrade gracefully without them.
async function readCurrentResources(
  medplum: ReturnType<typeof useMedplum>,
  refs: string[]
): Promise<Record<string, Resource>> {
  const results = await Promise.allSettled(
    refs.map(async (ref) => {
      const [resourceType, id] = ref.split('/')
      return medplum.readResource(
        resourceType as Resource['resourceType'],
        id ?? ''
      )
    })
  )
  const current: Record<string, Resource> = {}
  for (const [index, result] of results.entries()) {
    const ref = refs[index]
    if (ref && result.status === 'fulfilled') {
      current[ref] = result.value
    }
  }
  return current
}

export interface QueuedEditsReviewCardProps {
  // The persisted proposal Bundle's reference ("Bundle/<id>"). The Bundle is the
  // self-contained review record: it carries the proposed entries, the AI
  // summary, and the reviewer's per-entry decisions + final committer.
  bundleReference: string | undefined
  // When true, render the inline "save this review for later" affordance — an
  // icon button that promotes the Bundle into a QueuedEditsReviewTask (opening
  // the task composer to confirm + assign an owner), switching to a link to that
  // task once filed. Set only where the review has no orchestrating Task yet
  // (the inline ai-chat case); the inbox/task surface is already a Task.
  taskable?: boolean
  // Fired once when the review reaches its terminal committed state (the
  // reviewer applies, having approved and/or rejected each entry) with the
  // structured per-entry outcome. The hook for surfaces that must learn the
  // final decision — the AI chat feeds it back as the proposal tool's output so
  // a later turn knows what the human decided instead of re-proposing. Not
  // fired for intermediate per-entry toggles, only the terminal commit.
  onCommit?: (outcome: ProposalCommitOutcome) => void
}

// Bundle-driven review surface for a queued-edits proposal: loads the Bundle and
// the current state of every touched resource, renders the shared
// QueuedEditsReview diff surface, and on confirm applies the approved entries,
// records the decisions ON THE BUNDLE, and writes the review Provenance. Used
// directly inline in the AI chat (no Task) and wrapped by
// QueuedEditsReviewTaskCard for the inbox (Task orchestration).
export function QueuedEditsReviewCard({
  bundleReference,
  taskable = false,
  onCommit,
}: QueuedEditsReviewCardProps): JSX.Element {
  const medplum = useMedplum()
  const queryClient = useQueryClient()
  const isReadOnly = useIsReadOnly()
  const practitioner = useCurrentPractitioner()
  const { ai } = useSeenApi()

  // Filter, group-expansion, and mixed-owner group state live here, above the versionId-keyed
  // QueuedEditsReview, so they survive that child's remount on every decision
  // write (which re-seeds decision state). Held internally the filters would
  // reset to "All" and every opened group would snap shut on each decision.
  const [activeTab, setActiveTab] = useState<string>(QUEUED_EDITS_ALL_TAB)
  const [activeOwner, setActiveOwner] = useState<string | undefined>()
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])
  const [ungroupedGroups, setUngroupedGroups] = useState<string[]>([])

  const bundleId = bundleReference?.split('/')[1]

  // Keyed like useFhirRead (resourceType-first) so useSyncCache's message
  // handler, which matches incoming subscription messages by `findAll({ queryKey:
  // [resourceType] })` prefix, routes live Bundle updates here. The meta opts the
  // query into websocket subscription; the `_id` queryParam takes the handler's
  // refetch-on-match fallback. Without both, a concurrent reviewer's
  // decision/summary writes to the Bundle stay invisible until an unrelated
  // invalidation or a stale-window refocus refetch.
  const bundleQuery = useQuery({
    queryKey: cacheKeys.read('Bundle', bundleId ?? ''),
    enabled: Boolean(bundleId),
    queryFn: async () => medplum.readResource('Bundle', bundleId ?? ''),
    meta: {
      queryParams: { _id: bundleId },
      resourceType: 'Bundle',
    },
  })
  const bundle = bundleQuery.data

  // A completed review records the pinned version each decided entry's diff was
  // computed against (approved + rejected alike). When present, the read-only
  // summary diffs against those exact versions so the before/after stays
  // accurate even after the resources are edited post-review. Active (not-done)
  // reviews always diff against live state. "Done" = a final committer is
  // recorded on the Bundle.
  const isDone = bundle ? Boolean(getFinalCommittedAt(bundle)) : false
  const fromVersions = bundle ? entryFromVersions(bundle) : []
  const usePinnedBaseline = isDone && fromVersions.length > 0

  // The references of the current resources the proposed bundle mutates. Each is
  // read with its own query (keyed like useFhirRead) so useSyncCache opts each
  // into realtime websocket sync — a concurrent edit to a touched resource flows
  // straight into the diff instead of going stale until refetch. Skipped
  // entirely when a pinned baseline is used (the diff is frozen at apply time).
  const currentRefs = useMemo(
    () => (bundle && !usePinnedBaseline ? mutatedReferences(bundle) : []),
    [bundle, usePinnedBaseline]
  )

  const currentQueries = useQueries({
    queries: currentRefs.map((ref) => {
      const [resourceType, id] = ref.split('/') as [ResourceType, string]
      return {
        queryKey: cacheKeys.read(resourceType, id),
        // Missing/deleted resources are expected — the differ degrades
        // gracefully, so a failed read just contributes no `current` entry.
        queryFn: async () => medplum.readResource(resourceType, id),
        meta: {
          queryParams: { _id: id },
          resourceType,
        },
      }
    }),
  })

  // Read the pinned baseline versions for a completed review. Keyed by the
  // unversioned `ResourceType/id` so the resulting `current` map matches the
  // differ's expectation (a deleted-then-purged version just contributes no
  // entry — the differ degrades gracefully). Disabled when not using a pinned
  // baseline so active reviews don't fire history reads.
  const pinnedQueries = useQueries({
    queries: fromVersions.map((ref) => {
      const { resourceType, id, versionId } = parseHistoryReferenceString(
        ref.reference ?? ''
      )
      return {
        queryKey: cacheKeys.readVersion(
          resourceType as ResourceType,
          id,
          versionId,
          'Resource'
        ),
        enabled: usePinnedBaseline,
        queryFn: async () =>
          medplum.readVersion(resourceType as ResourceType, id, versionId),
      }
    }),
  })

  const liveDataKey = currentQueries.map((q) => q.dataUpdatedAt).join(',')
  const pinnedDataKey = pinnedQueries.map((q) => q.dataUpdatedAt).join(',')

  const currentPending = usePinnedBaseline
    ? pinnedQueries.some((q) => q.isPending)
    : currentQueries.some((q) => q.isPending)

  const current = useMemo(() => {
    const map: Record<string, Resource> = {}
    if (usePinnedBaseline) {
      for (const query of pinnedQueries) {
        const data = query.data
        if (data) {
          map[`${data.resourceType}/${data.id}`] = data
        }
      }
      return map
    }
    currentRefs.forEach((ref, index) => {
      const data = currentQueries[index]?.data
      if (data) {
        map[ref] = data
      }
    })
    return map
    // The *DataKey deps capture per-query data changes (incl. live sync
    // updates); usePinnedBaseline switches the source.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRefs, liveDataKey, pinnedDataKey, usePinnedBaseline])

  const changeSetsInput: BundleToChangeSetsInput | undefined = useMemo(
    () => (bundle ? { bundle, current } : undefined),
    [bundle, current]
  )
  // Recomputed here (memoized inside QueuedEditsReview too) to map the approved
  // change ids back to their bundle entries at apply time. `alreadyApplied`
  // entries (their change landed outside the review) are excluded from
  // `changes`, so they are never recorded as approved/rejected — the reviewer
  // never decided them.
  const { changes, alreadyApplied } = useBundleChangeSets(changeSetsInput)

  const applyMutation = useMutation({
    mutationFn: async (approvedIds: string[]) => {
      if (!bundle) {
        throw new Error('Proposed bundle is not loaded')
      }
      const approved = changes.filter((c) => approvedIds.includes(c.id))
      const rejected = changes.filter((c) => !approvedIds.includes(c.id))
      const approvedFullUrls = new Set(
        approved.map((c) => c.fullUrl).filter(Boolean)
      )
      // Build the apply bundle from the approved entries, stripped of decision
      // extensions so the review metadata never reaches the server write. Clone
      // — rebasing mutates the PATCH payloads and the bundle is shared with the
      // react-query cache.
      const applyBundle: Bundle = stripEntryDecisions({
        ...structuredClone(bundle),
        entry: (bundle.entry ?? []).filter(
          (entry) => entry.fullUrl && approvedFullUrls.has(entry.fullUrl)
        ),
      })
      const entryHandle = (c: (typeof changes)[number]): string =>
        c.reference ?? c.fullUrl ?? c.id

      // The version each decided entry's diff was computed against, keyed by
      // entry handle, recorded on the bundle so the completed diff reads a stable
      // baseline for BOTH outcomes. Approved entries pin the version executeBatch
      // actually applied against; rejected entries pin the live version their
      // (discarded) diff was shown against — captured here before completing.
      const fromVersionByEntry: Record<string, Reference> = {}

      // Each approved entry's server-resolved `ResourceType/id`, keyed by
      // fullUrl — populated from the apply response below. Carries the
      // newly-created id for approved creates so the commit outcome can report
      // resources that only exist after the write.
      const appliedReferences: Record<string, string> = {}

      // Rejected entries are never executed, so their baseline is the current
      // state the reviewer saw — read it off the already-loaded `current` map.
      for (const c of rejected) {
        const resource = c.reference ? current[c.reference] : undefined
        if (resource?.meta?.versionId) {
          fromVersionByEntry[entryHandle(c)] = {
            reference: createReferenceStringWithHistory(resource),
          }
        }
      }

      if (applyBundle.entry?.length) {
        // The stored bundle is from proposal time — any intervening write
        // would fail its version preconditions, and some entries' effects may
        // have landed outside the review since the diff was rendered. Re-read
        // the touched resources and run the per-verb rebase: drop entries
        // whose effect is already present (PATCH no-op / DELETE target
        // absent — the end state still matches what the reviewer approved),
        // and re-point remaining patches at the fresh versions (element-keyed
        // ops re-resolve safely against the newer version).
        const fresh = await readCurrentResources(
          medplum,
          mutatedReferences(applyBundle)
        )
        const { bundle: executable } = rebaseProposalApplyBundle(
          applyBundle,
          fresh
        )
        if (executable.entry?.length) {
          // The proposed bundle already carries the queued-edits origin tag on
          // every entry (stamped at proposal time via the mutation builder), so
          // each applied resource's history links back to this review — no
          // apply-time stamping needed here.
          // executeBatch stamps each PATCH response entry with the base version
          // its patch was finally conditioned on — the authoritative "applied
          // from" version, resolved after any internal rebase onto a newer
          // version on a `test /meta/versionId` conflict. Map each back to its
          // approved entry handle (the versioned ref's base is
          // `ResourceType/id`, which is the entry handle for mutation entries).
          const result = await medplum.executeBatch(executable)
          for (const ref of appliedFromVersionRefs(result)) {
            const base = ref.reference?.split('/_history/')[0]
            if (base) {
              fromVersionByEntry[base] = ref
            }
          }
          // Resolve each approved entry's server reference (created ids
          // included) from the response, keyed by fullUrl, for the commit
          // outcome.
          Object.assign(
            appliedReferences,
            appliedReferencesByFullUrl(executable, result)
          )
        }
      }

      // Record the decisions + final committer ON THE BUNDLE — it is the durable
      // review record. Per-entry deciders are preserved by recordEntryDecisions;
      // the confirming practitioner is only the apply-time fallback for any entry
      // never toggled. Re-read → record → update under a conflict-retry loop:
      // useBundleDecisionWriter may land an in-flight decision write between this
      // read and update (a last-decision-then-confirm race), failing the
      // If-Match precondition. The batch already applied, so finalization must
      // not be lost — replaying against fresh state is safe since
      // fromVersionByEntry is fixed once executeBatch returns.
      for (let attempt = 0; attempt < FINALIZE_CONFLICT_RETRIES; attempt++) {
        const latest = await medplum.readResource('Bundle', bundle.id ?? '')
        recordEntryDecisions(latest, {
          approved: approved.map(entryHandle),
          rejected: rejected.map(entryHandle),
          by: practitioner.reference,
          fromVersionByEntry,
        })
        setFinalCommitter(latest, practitioner.reference)
        try {
          await medplum.updateResource(latest)
          break
        } catch (error) {
          if (attempt === FINALIZE_CONFLICT_RETRIES - 1) {
            throw error
          }
        }
      }

      // Complete the orchestrating review task, if one references this bundle
      // (the inbox/async case, or an inline review the user promoted with "save
      // for later"). The bundle is the durable record; the task is just the
      // inbox handle, so closing it on apply keeps the inbox in sync.
      const reviewTask = await medplum.searchOneResourceClass(
        QueuedEditsReviewTask,
        QueuedEditsReviewTask.byBundle({ reference: `Bundle/${bundle.id}` })
      )
      if (reviewTask && !reviewTask.isDone) {
        await medplum.patchResourceInstance(reviewTask, (t) => {
          t.state = 'done'
        })
      }

      // Attribute the review outcome to the deciding practitioner — in both the
      // approve and the all-rejected case (rejecting everything is itself a
      // decision). Per-entry outcomes live on the Bundle. Targets the Bundle plus
      // the orchestrating task when one exists.
      const targets: Reference[] = [
        { reference: `Bundle/${bundle.id}` },
        ...(reviewTask ? [reviewTask.reference] : []),
      ]
      for (const target of targets) {
        await medplum.createResource(
          new ExtendedProvenance({
            target,
            who: practitioner.reference,
            type: ProvenanceParticipantType.AUTHOR,
            activity: provenanceActivityQueuedEditsReviewed,
          }).toFhirResource()
        )
      }

      // The structured outcome a caller (the AI chat) reads back to learn what
      // the human decided. Built from the same approved/rejected split the apply
      // used, so it always matches what was written.
      const commitOutcome = buildProposalCommitOutcome(
        changes,
        approvedIds,
        appliedReferences
      )
      return { approvedCount: approved.length, commitOutcome }
    },
    onSuccess: ({ approvedCount, commitOutcome }) => {
      toast.success(
        approvedCount > 0
          ? 'Approved changes applied.'
          : 'Review recorded — all changes rejected.'
      )
      onCommit?.(commitOutcome)
    },
    onError: (error) => {
      toast.error(`Failed to apply changes: ${normalizeErrorString(error)}`)
    },
    onSettled: () => {
      void queryClient.invalidateQueries()
    },
  })

  // --- AI summary drift refresh -------------------------------------------
  // When entries dropped out as already-applied, the stored summary/intents
  // still describe them — a comprehension layer that no longer matches the
  // rendered diff. Regenerate over the remaining entries (host AI endpoint)
  // and persist the result on the bundle, recording which handles the new
  // summary omitted so the same drift never re-triggers a regeneration.
  const droppedHandles = useMemo(
    () => alreadyApplied.map((c) => c.reference ?? c.fullUrl ?? c.id).sort(),
    [alreadyApplied]
  )
  const refreshAttemptedFor = useRef<string>(undefined)
  const refreshSummaryMutation = useMutation({
    mutationFn: async () => {
      if (!(bundle?.id && ai)) {
        return
      }
      const response = await ai.refreshQueuedEditsSummary({
        bundleId: bundle.id,
        omitEntryHandles: droppedHandles,
      })
      // Persist under the same conflict-retry budget as the finalize write —
      // decision toggles race this update.
      for (let attempt = 0; attempt < FINALIZE_CONFLICT_RETRIES; attempt++) {
        const latest = await medplum.readResource('Bundle', bundle.id)
        setBundleAiSummary(latest, response.summary)
        setEntryIntents(latest, response.intents)
        setBundleAiSummaryOmittedEntries(latest, droppedHandles)
        try {
          await medplum.updateResource(latest)
          break
        } catch (error) {
          if (attempt === FINALIZE_CONFLICT_RETRIES - 1) {
            throw error
          }
        }
      }
    },
    onError: (error) =>
      console.warn('Failed to refresh queued-edits AI summary', error),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: cacheKeys.read('Bundle', bundleId ?? ''),
      })
    },
  })
  useEffect(() => {
    if (
      !bundle ||
      isDone ||
      isReadOnly ||
      !ai ||
      droppedHandles.length === 0 ||
      // Everything dropped — nothing left to summarize.
      changes.length === 0 ||
      // The stored summary already accounts for exactly this drift.
      isEqual(droppedHandles, getBundleAiSummaryOmittedEntries(bundle))
    ) {
      return
    }
    // One attempt per drift signature — a failed regeneration must not loop.
    const signature = droppedHandles.join('|')
    if (refreshAttemptedFor.current === signature) {
      return
    }
    refreshAttemptedFor.current = signature
    refreshSummaryMutation.mutate()
  }, [
    bundle,
    isDone,
    isReadOnly,
    ai,
    droppedHandles,
    changes.length,
    refreshSummaryMutation.mutate,
  ])

  // Serialized, coalescing writer — persists each decision toggle to the bundle
  // without stampeding the server with racing version-tested writes. The
  // in-memory state remains the live source of truth; failures are non-fatal.
  const writeDecision = useBundleDecisionWriter(
    bundle ?? { resourceType: 'Bundle', type: 'batch' },
    practitioner.reference,
    (error) => console.warn('Failed to save review decision', error)
  )

  if (bundleQuery.error) {
    return (
      <p className="text-sm text-destructive">
        Could not load the proposed changes:{' '}
        {normalizeErrorString(bundleQuery.error)}
      </p>
    )
  }

  // `!applyMutation.isSuccess` guards an in-session completion: once apply
  // succeeds, onSettled invalidates queries and the refetched bundle flips
  // `usePinnedBaseline` to true, so the newly-enabled pinned reads are briefly
  // `isPending`. Without this, the already-shown summary would flash back to this
  // loader until those reads resolve. Initial loads of completed reviews (no
  // isSuccess) still show the loader while pinned data is fetched.
  if (!bundle || (currentPending && !applyMutation.isSuccess)) {
    return (
      <p className="flex items-center text-sm text-muted-foreground">
        <Spinner /> Loading proposed changes…
      </p>
    )
  }

  // Once applied — whether in this session or an earlier one — show the collapsed
  // read-only summary (AI overview, outcome, who was involved, who applied it)
  // instead of the editable review surface.
  if ((isDone || applyMutation.isSuccess) && !applyMutation.isPending) {
    return (
      <QueuedEditsReviewSummary
        bundle={bundle}
        changes={changes}
        // For a single change the per-entry intent already says it all — only
        // surface the bundle-level overview when there's more than one.
        aiSummary={changes.length > 1 ? getBundleAiSummary(bundle) : undefined}
      />
    )
  }

  const phase: QueuedEditsReviewPhase = applyMutation.isPending
    ? 'processing'
    : 'review'

  if (isReadOnly && phase === 'review') {
    return (
      <p className="text-sm text-muted-foreground">
        These proposed changes are pending review.
      </p>
    )
  }

  // The stable handle a decision is recorded under on the bundle — the bundle
  // entry reference / fullUrl, falling back to the change id. Same mapping used
  // at apply time so rehydration round-trips.
  const entryHandleOf = (change: (typeof changes)[number]): string =>
    change.reference ?? change.fullUrl ?? change.id

  // Rehydrate in-memory decisions (who made them, and when) from what the bundle
  // already recorded, mapping entry handles back to change ids.
  const initialDecisions: Record<string, ResourceDecision> = {}
  const initialDeciders: Record<string, Reference<Practitioner>> = {}
  const initialDecidedAt: Record<string, string> = {}
  const decisionByHandle = new Map(
    getEntryDecisions(bundle).map((d) => [d.entry, d])
  )
  for (const change of changes) {
    const recorded = decisionByHandle.get(entryHandleOf(change))
    if (recorded) {
      initialDecisions[change.id] = recorded.decision
      if (recorded.by) {
        initialDeciders[change.id] = recorded.by
      }
      if (recorded.at) {
        initialDecidedAt[change.id] = recorded.at
      }
    }
  }

  return (
    <QueuedEditsReview
      key={`${bundle.id}_${bundle.meta?.versionId}`}
      title="Proposed changes"
      taskable={taskable}
      bundle={bundle}
      current={current}
      phase={phase}
      activeTab={activeTab}
      onActiveTabChange={setActiveTab}
      activeOwner={activeOwner}
      onActiveOwnerChange={setActiveOwner}
      expandedGroups={expandedGroups}
      onExpandedGroupsChange={setExpandedGroups}
      ungroupedGroups={ungroupedGroups}
      onUngroupedGroupsChange={setUngroupedGroups}
      initialDecisions={initialDecisions}
      initialDeciders={initialDeciders}
      initialDecidedAt={initialDecidedAt}
      currentDecider={practitioner.reference}
      onDecisionChange={(id, decision) => {
        const change = changes.find((c) => c.id === id)
        if (change) {
          writeDecision(entryHandleOf(change), decision)
        }
      }}
      onBulkDecisionChange={(decisions) => {
        for (const change of changes) {
          writeDecision(entryHandleOf(change), decisions[change.id])
        }
      }}
      onConfirm={(approvedIds) => applyMutation.mutate(approvedIds)}
    />
  )
}
